import { useLottiePlayer } from '@starsoft/common/hooks';
import useTranslation from 'next-translate/useTranslation';
import { useForm, UseFormReturn } from 'react-hook-form';
import styles from './styles.module.scss';
import {
  Button,
  ErrorCard,
  TransTextWithDefault,
} from '@starsoft/common/components';
import UserEmailFormVerify from '../Verify';
import UserEmailFormSkeleton from '../Skeleton';

import { UserEmailVerifyInputDto } from '@starsoft/common/dto';
import { UserEmailFormProps } from './props';
import { useUserEmailsForm } from '@starsoft/common/hooks/user-emails/useUserEmailsForm';

/**
 * UserEmailForm Component
 * This component is responsible for rendering the user email form, handling different states like error, skeleton, and loading.
 * It uses various hooks to manage form state, translations, and animations.
 *
 * @param {UserEmailFormProps} props - The properties for the component.
 * @param {boolean} props.isContainerColor - Indicates if the container should have a color.
 * @param {boolean} props.useMinimalForm - Indicates if the minimal form should be used.
 *
 * @returns {JSX.Element} The rendered user email form component.
 *
 * @example
 * // Usage example:
 * <UserEmailForm isContainerColor={true} useMinimalForm={false} />
 */
export default function UserEmailForm({
  isContainerColor,
  useMinimalForm,
}: UserEmailFormProps) {
  // Translation hook for the 'account' namespace
  const { t } = useTranslation('account');

  // Initialize form methods using react-hook-form
  const methods: UseFormReturn<UserEmailVerifyInputDto> =
    useForm<UserEmailVerifyInputDto>();

  // Use custom hook to manage form state and actions
  const {
    isSkeleton,
    error,
    isPendingVerifyUserEmail,
    handleError,
    userEmail,
    isLoadingEmails,
    isSubmitDisabled,
  } = useUserEmailsForm({ methods, useMinimalForm });

  // Initialize Lottie animation player with dependencies
  const { lottieAnimationRef } = useLottiePlayer({
    path: '/email-validated.json',
    dependecyArray: [isSkeleton, isLoadingEmails],
  });

  // Render error state with an ErrorCard component
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.container__content}>
          <ErrorCard error={error} refetch={handleError} />
        </div>
      </div>
    );
  }

  // Render skeleton state or loading state with a UserEmailFormSkeleton component
  if (isSkeleton || isLoadingEmails) {
    return <UserEmailFormSkeleton />;
  }

  // Render the form content
  return (
    <>
      <div className={styles.container__content}>
        {!useMinimalForm ? (
          <div className={styles.container__content__column}>
            <div
              className={styles.container__content__lottie}
              ref={lottieAnimationRef}
            />
            <p className={styles.container__content__description}>
              <TransTextWithDefault
                text={t('verify_user_email_description', {
                  email: userEmail?.email,
                })}
              />
            </p>
          </div>
        ) : null}

        <UserEmailFormVerify isContainerColor={isContainerColor} />
      </div>

      {!useMinimalForm && (
        <div className={styles.container__button}>
          <Button
            type="submit"
            isloading={isPendingVerifyUserEmail}
            disabled={isSubmitDisabled}
          >
            {t('verify')}
          </Button>
        </div>
      )}
    </>
  );
}
